import type { LatLng } from "leaflet";
import _ from "lodash";
import { type RefObject, useCallback } from "react";

import type { MapHandle, MapMode } from "@/types";

/**
 * Props for the `useMapClick` hook.
 */
interface MapClickProps {
    /**
     * Callback to change the current map mode.
     */
    changeMode: (name: string, props: Record<string, unknown>) => void;
    /**
     * The ref holding the imperative handle of the `Map` component.
     */
    mapRef: RefObject<MapHandle | null>;
    /**
     * The active map mode.
     */
    mode: MapMode;
    /**
     * Callback that places new fixtures at the clicked point.
     */
    onAddFixtures: (point: LatLng) => void;
    /**
     * Callback to paste lights at the clicked point.
     */
    paste: (point: LatLng) => void;
    /**
     * Whether paste mode is active.
     */
    pasteModeActivated: boolean;
    /**
     * Callback to set the highlight position.
     */
    setHighlight: (highlight: {
        column?: number | null;
        row?: number | null;
    }) => void;
}

/**
 * Routes a click on the map to the highlight, paste, or fixture placement flow
 * depending on the active mode.
 *
 * @param params - The map handle ref, mode state, and mode-specific callbacks.
 * @returns The map click handler.
 */
export const useMapClick = ({
    changeMode,
    mapRef,
    mode,
    onAddFixtures,
    paste,
    pasteModeActivated,
    setHighlight,
}: MapClickProps) => {
    const onMapClick = useCallback(
        (point: LatLng) => {
            if (!mapRef.current) return;

            const { map } = mapRef.current;

            if (mode.name === "highlight") {
                setHighlight({
                    [_.lowerCase(mode.props.direction)]:
                        mode.props.direction === "Column"
                            ? point.lng
                            : point.lat,
                });

                changeMode("view", {});

                return;
            }

            if (pasteModeActivated) {
                paste(point);
                return;
            }

            if (mode.name === "add" && !map.movingLights && !map.isDragging) {
                onAddFixtures(point);
            } else if (map.movingLights) {
                map.movingLights = false;
            }
        },
        [
            mapRef,
            mode.name,
            mode.props,
            pasteModeActivated,
            setHighlight,
            changeMode,
            paste,
            onAddFixtures,
        ],
    );

    return { onMapClick };
};
